<script>
  import { selectedStudy } from '$stores/selectionStore';
  import { userRoles, userReadOnly } from '$stores/sessionStore';
  import { studyProperties } from '$stores/configStore';
  import { hasAccessUiAction } from '$utils/permissionUtils.js';

  $: isAdmin = hasAccessUiAction('apidocs.access', $userRoles, $studyProperties, $userReadOnly);

  const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api/v1';

  let activeSection = 'authentication';
  let isLoadingKeyMetadata = false;
  let isGeneratingKey = false;
  let activeLanguage = 'curl';
  let apiAccess = null;
  let generatedApiKey = '';
  let apiError = '';
  let apiSuccess = '';
  let lastLoadedStudyId = null;

  // Try it out
  let trySubjectId = '';
  let tryReadingType = 'CGM';
  let tryStartDate = '';
  let tryEndDate = '';
  let isTrying = false;
  let tryStatusCode = null;
  let tryResponse = null;
  let tryResponseError = '';

  const navSections = [
    { id: 'authentication', title: 'Authentication', description: 'Manage study API key for external access.' },
    { id: 'glucoseReadings', title: 'Glucose Readings', description: 'Query glucose readings with optional subject/date filtering.' }
  ];

  $: protocol = $selectedStudy?.protocolNumber || '';

  $: tryParams = (() => {
    const params = new URLSearchParams();
    if (protocol) params.set('protocol', protocol);
    if (trySubjectId.trim()) params.set('subjectId', trySubjectId.trim());
    if (tryStartDate) params.set('startDate', new Date(tryStartDate).toISOString());
    if (tryEndDate) {
      const d = new Date(tryEndDate);
      d.setUTCHours(23, 59, 59, 999);
      params.set('endDate', d.toISOString());
    }
    if (tryReadingType) params.set('type', tryReadingType);
    params.set('pageSize', '10');
    return params;
  })();

  $: proxyUrl = `/api/v1/glucoseReadings/external?${tryParams}`;
  $: externalUrl = `${apiBase}/glucoseReadings/external?${tryParams}`;

  $: curlExample = `curl "${externalUrl}" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_STUDY_API_KEY"`;

  $: nodeExample = (() => {
    const paramsObj = Object.fromEntries(tryParams.entries());
    return `const params = new URLSearchParams(${JSON.stringify(paramsObj, null, 2)});

const response = await fetch(\`${apiBase}/glucoseReadings/external?\${params}\`, {
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': 'YOUR_STUDY_API_KEY'
  }
});

const data = await response.json();
console.log(data);`;
  })();

  function resetMessages() {
    apiError = '';
    apiSuccess = '';
  }

  async function copyToClipboard(value) {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      apiSuccess = 'Copied to clipboard.';
    } catch {
      apiError = 'Copy failed. Please copy manually.';
    }
  }

  async function fetchApiKeyMetadata(studyId) {
    isLoadingKeyMetadata = true;
    resetMessages();
    try {
      const response = await fetch(`/api/v1/studyProperties/${studyId}/api-key`);
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.message || 'Failed to load API key metadata.');
      apiAccess = payload?.data || null;
    } catch (error) {
      apiError = error.message || 'Failed to load API key metadata.';
      apiAccess = null;
    } finally {
      isLoadingKeyMetadata = false;
    }
  }

  async function generateApiKey() {
    if (!isAdmin) { apiError = 'Admin role required'; return; }
    if (!$selectedStudy?._id) return;
    isGeneratingKey = true;
    generatedApiKey = '';
    resetMessages();
    try {
      const response = await fetch(`/api/v1/studyProperties/${$selectedStudy._id}/api-key/generate`, { method: 'POST' });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.message || 'Failed to generate API key.');
      generatedApiKey = payload?.data?.apiKey || '';
      apiSuccess = payload?.message || 'API key ready. Save it now; it will not be shown again.';
      await fetchApiKeyMetadata($selectedStudy._id);
    } catch (error) {
      apiError = error.message || 'Failed to generate API key.';
    } finally {
      isGeneratingKey = false;
    }
  }

  async function executeRequest() {
    tryResponseError = '';
    tryResponse = null;
    tryStatusCode = null;

    if (!protocol) { tryResponseError = 'Select a study first.'; return; }

    isTrying = true;
    try {
      const response = await fetch(proxyUrl);
      tryStatusCode = response.status;
      const data = await response.json();
      tryResponse = JSON.stringify(data, null, 2);
      if (!response.ok) tryResponseError = data?.message || 'Request failed.';
    } catch (error) {
      tryResponseError = error.message || 'Request failed.';
    } finally {
      isTrying = false;
    }
  }

  $: if ($selectedStudy?._id && $selectedStudy._id !== lastLoadedStudyId) {
    lastLoadedStudyId = $selectedStudy._id;
    generatedApiKey = '';
    fetchApiKeyMetadata($selectedStudy._id);
  }

  $: if (!$selectedStudy?._id) {
    lastLoadedStudyId = null;
    apiAccess = null;
    generatedApiKey = '';
  }
</script>

<div class="min-h-screen bg-white dark:bg-gray-800">
  <div class="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-5 py-4">
    <h1 class="text-lg font-bold text-gray-800 dark:text-white">API Documentation</h1>
    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
      {#if $selectedStudy?.protocolNumber}
        Study: <span class="font-medium text-gray-700 dark:text-gray-200">{$selectedStudy.protocolNumber}</span>
      {:else}
        No study selected
      {/if}
    </p>
  </div>

  <div class="grid grid-cols-12 gap-0">
    <aside class="col-span-3 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 min-h-[calc(100vh-80px)]">
      <div class="p-4 space-y-4">
        {#each navSections as section (section.id)}
          <button
            class="w-full rounded border px-3 py-2 text-left text-sm transition-colors {activeSection === section.id
              ? 'border-teal-500 bg-white dark:bg-gray-800 text-teal-700 dark:text-teal-300'
              : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-800'}"
            on:click={() => (activeSection = section.id)}
          >
            <div class="font-medium">{section.title}</div>
            <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">{section.description}</div>
          </button>
        {/each}
      </div>
    </aside>

    <main class="col-span-9 bg-white dark:bg-gray-800">
      <div class="p-5 space-y-6">

        {#if activeSection === 'authentication'}
          <!-- Authentication / API Key Management -->
          <section class="rounded border border-gray-200 dark:border-gray-700 p-4">
            <h2 class="text-sm font-semibold uppercase text-gray-500 dark:text-gray-400">Study API Key</h2>
            <p class="mt-2 text-sm text-gray-600 dark:text-gray-300">
              Each study has a single API key used to authenticate external requests. The key is bound to its study and can only query data for that protocol.
              Include it as an <code class="text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">x-api-key</code> header on every request.
            </p>

            {#if !$selectedStudy?._id}
              <p class="mt-4 text-sm text-amber-700 dark:text-amber-300">
                Select a study to manage its API key.
              </p>
            {:else}
              <div class="mt-4 grid gap-3 sm:grid-cols-2">
                <div class="rounded border border-gray-200 dark:border-gray-700 p-3">
                  <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Key status</p>
                  {#if isLoadingKeyMetadata}
                    <p class="mt-1 text-sm text-gray-700 dark:text-gray-200">Loading...</p>
                  {:else if apiAccess?.hasKey}
                    <p class="mt-1 text-sm text-gray-700 dark:text-gray-200">
                      Active: <span class="font-mono">{apiAccess.keyPrefix}...</span>
                    </p>
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Last rotated: {apiAccess.lastRotatedAt || 'N/A'}
                    </p>
                  {:else}
                    <p class="mt-1 text-sm text-gray-700 dark:text-gray-200">No key generated yet.</p>
                  {/if}
                </div>

                <div class="rounded border border-gray-200 dark:border-gray-700 p-3">
                  <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Actions</p>
                  <div class="mt-2 flex flex-wrap gap-2">
                    <button
                      class="rounded bg-teal-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                      on:click={generateApiKey}
                      disabled={!isAdmin || isGeneratingKey}
                    >
                      {#if isGeneratingKey}
                        Generating...
                      {:else if apiAccess?.hasKey}
                        Rotate Key
                      {:else}
                        Generate Key
                      {/if}
                    </button>
                  </div>
                  {#if !isAdmin}
                    <p class="mt-2 text-xs text-gray-400 dark:text-gray-500">Admin role required.</p>
                  {/if}
                </div>
              </div>

              {#if generatedApiKey}
                <div class="mt-3 rounded border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-900/20 p-3">
                  <p class="text-xs font-semibold uppercase text-amber-800 dark:text-amber-300">
                    New API key (shown once)
                  </p>
                  <div class="mt-2 flex items-center gap-2">
                    <code class="block flex-1 overflow-x-auto rounded bg-white dark:bg-gray-900 p-2 text-xs text-gray-800 dark:text-gray-100">{generatedApiKey}</code>
                    <button
                      class="rounded border border-amber-400 px-2 py-1 text-xs text-amber-800 dark:text-amber-300"
                      on:click={() => copyToClipboard(generatedApiKey)}
                    >
                      Copy
                    </button>
                  </div>
                </div>
              {/if}
            {/if}

            {#if apiError}
              <p class="mt-3 text-sm text-red-700 dark:text-red-300">{apiError}</p>
            {/if}
            {#if apiSuccess}
              <p class="mt-3 text-sm text-green-700 dark:text-green-300">{apiSuccess}</p>
            {/if}
          </section>

        {:else if activeSection === 'glucoseReadings'}
          <!-- Glucose Readings Endpoint -->
          <section class="rounded border border-gray-200 dark:border-gray-700 p-4">
            <div class="flex items-center gap-2">
              <span class="rounded bg-green-100 dark:bg-green-900/40 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-300">GET</span>
              <code class="text-sm text-gray-700 dark:text-gray-200">/api/v1/glucoseReadings/external</code>
            </div>
            <p class="mt-2 text-sm text-gray-600 dark:text-gray-300">
              Query glucose readings for the authenticated study. Subject, reading type (CGM vs SMBG vs all), and date range are optional filters.
              When <code class="text-[10px]">subjectId</code> is omitted, results are limited to a recent time window on <code class="text-[10px]">read</code>
              (intersected with optional <code class="text-[10px]">startDate</code> / <code class="text-[10px]">endDate</code>); see
              <code class="text-[10px]">filters.recentWindowHours</code> in the response for the window length in hours.
            </p>

            <!-- Parameter table -->
            <div class="mt-4 overflow-hidden rounded border border-gray-200 dark:border-gray-700">
              <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                <thead class="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th class="px-3 py-2 text-left font-semibold text-gray-500 dark:text-gray-400">Parameter</th>
                    <th class="px-3 py-2 text-left font-semibold text-gray-500 dark:text-gray-400">Type</th>
                    <th class="px-3 py-2 text-left font-semibold text-gray-500 dark:text-gray-400">Required</th>
                    <th class="px-3 py-2 text-left font-semibold text-gray-500 dark:text-gray-400">Description</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                  <tr>
                    <td class="px-3 py-2 font-mono">protocol</td>
                    <td class="px-3 py-2">query</td>
                    <td class="px-3 py-2">no</td>
                    <td class="px-3 py-2">Inferred from API key. Optional; rejected if it doesn't match the key's study.</td>
                  </tr>
                  <tr>
                    <td class="px-3 py-2 font-mono">subjectId</td>
                    <td class="px-3 py-2">query</td>
                    <td class="px-3 py-2">no</td>
                    <td class="px-3 py-2">Filter by subject identifier. If omitted, the server only returns readings whose <code class="text-[10px]">read</code> time falls within the recent window described by <code class="text-[10px]">filters.recentWindowHours</code> (and within optional date bounds).</td>
                  </tr>
                  <tr>
                    <td class="px-3 py-2 font-mono">type</td>
                    <td class="px-3 py-2">query</td>
                    <td class="px-3 py-2">no</td>
                    <td class="px-3 py-2">
                      <code class="text-[10px]">CGM</code> (default), <code class="text-[10px]">SMBG</code> (non-CGM readings), or
                      <code class="text-[10px]">ALL</code>. Response normalizes stored types to <code class="text-[10px]">CGM</code> or
                      <code class="text-[10px]">SMBG</code>.
                    </td>
                  </tr>
                  <tr>
                    <td class="px-3 py-2 font-mono">startDate</td>
                    <td class="px-3 py-2">query</td>
                    <td class="px-3 py-2">no</td>
                    <td class="px-3 py-2">UTC ISO datetime lower bound for read timestamp.</td>
                  </tr>
                  <tr>
                    <td class="px-3 py-2 font-mono">endDate</td>
                    <td class="px-3 py-2">query</td>
                    <td class="px-3 py-2">no</td>
                    <td class="px-3 py-2">UTC ISO datetime upper bound for read timestamp.</td>
                  </tr>
                  <tr>
                    <td class="px-3 py-2 font-mono">page</td>
                    <td class="px-3 py-2">query</td>
                    <td class="px-3 py-2">no</td>
                    <td class="px-3 py-2">Page number, default 1.</td>
                  </tr>
                  <tr>
                    <td class="px-3 py-2 font-mono">pageSize</td>
                    <td class="px-3 py-2">query</td>
                    <td class="px-3 py-2">no</td>
                    <td class="px-3 py-2">Items per page, default 100, max 1000.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Try it out -->
            <div class="mt-5 rounded border border-gray-200 dark:border-gray-700">
              <div class="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60 px-4 py-2">
                <span class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Try it out</span>
                {#if !$selectedStudy?._id}
                  <span class="text-xs text-amber-600 dark:text-amber-400">Select a study to enable</span>
                {/if}
              </div>

              <div class="p-4 space-y-3">
                <div class="grid grid-cols-4 items-center gap-3">
                  <!-- svelte-ignore a11y-label-has-associated-control -->
                  <label class="text-xs font-medium text-gray-500 dark:text-gray-400 text-right">Protocol</label>
                  <div class="col-span-3">
                    <input
                      type="text"
                      value={protocol || 'No study selected'}
                      disabled
                      class="w-full rounded border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 px-2 py-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-not-allowed"
                    />
                  </div>
                </div>

                <div class="grid grid-cols-4 items-center gap-3">
                  <!-- svelte-ignore a11y-label-has-associated-control -->
                  <label class="text-xs font-medium text-gray-500 dark:text-gray-400 text-right">Subject ID</label>
                  <div class="col-span-3">
                    <input
                      type="text"
                      bind:value={trySubjectId}
                      placeholder="Optional"
                      class="w-full rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-xs text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-teal-500"
                    />
                  </div>
                </div>

                <div class="grid grid-cols-4 items-center gap-3">
                  <!-- svelte-ignore a11y-label-has-associated-control -->
                  <label class="text-xs font-medium text-gray-500 dark:text-gray-400 text-right" for="try-reading-type">Reading type</label>
                  <div class="col-span-3">
                    <select
                      id="try-reading-type"
                      bind:value={tryReadingType}
                      class="w-full max-w-xs rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-xs text-gray-800 dark:text-gray-100 focus:outline-none focus:border-teal-500"
                    >
                      <option value="CGM">CGM (default)</option>
                      <option value="SMBG">SMBG (non-CGM)</option>
                      <option value="ALL">ALL</option>
                    </select>
                  </div>
                </div>

                <div class="grid grid-cols-4 items-center gap-3">
                  <!-- svelte-ignore a11y-label-has-associated-control -->
                  <label class="text-xs font-medium text-gray-500 dark:text-gray-400 text-right">Start Date</label>
                  <div class="col-span-3">
                    <input
                      type="date"
                      bind:value={tryStartDate}
                      class="rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-xs text-gray-800 dark:text-gray-100 focus:outline-none focus:border-teal-500"
                    />
                  </div>
                </div>

                <div class="grid grid-cols-4 items-center gap-3">
                  <!-- svelte-ignore a11y-label-has-associated-control -->
                  <label class="text-xs font-medium text-gray-500 dark:text-gray-400 text-right">End Date</label>
                  <div class="col-span-3">
                    <input
                      type="date"
                      bind:value={tryEndDate}
                      class="rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-xs text-gray-800 dark:text-gray-100 focus:outline-none focus:border-teal-500"
                    />
                  </div>
                </div>

                <div class="grid grid-cols-4 items-start gap-3">
                  <!-- svelte-ignore a11y-label-has-associated-control -->
                  <label class="text-xs font-medium text-gray-500 dark:text-gray-400 text-right pt-1.5">Request URL</label>
                  <div class="col-span-3">
                    <code class="block break-all rounded bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-2 py-1.5 text-xs text-gray-700 dark:text-gray-300">{externalUrl}</code>
                  </div>
                </div>

                <div class="grid grid-cols-4 items-center gap-3">
                  <div></div>
                  <div class="col-span-3">
                    <button
                      class="rounded bg-teal-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-teal-700 disabled:opacity-50 transition-colors"
                      on:click={executeRequest}
                      disabled={isTrying || !protocol}
                    >
                      {isTrying ? 'Executing...' : 'Execute'}
                    </button>
                  </div>
                </div>

                {#if tryResponseError && !tryResponse}
                  <div class="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                    <p class="text-xs text-red-700 dark:text-red-300">{tryResponseError}</p>
                  </div>
                {/if}

                {#if tryResponse !== null}
                  <div class="rounded border border-gray-200 dark:border-gray-700">
                    <div class="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60 px-3 py-2">
                      <span class="text-xs font-semibold text-gray-500 dark:text-gray-400">Response</span>
                      <div class="flex items-center gap-2">
                        {#if tryStatusCode}
                          <span class="rounded px-1.5 py-0.5 text-xs font-mono font-medium {tryStatusCode < 300 ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'}">
                            {tryStatusCode}
                          </span>
                        {/if}
                        <button
                          class="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                          on:click={() => copyToClipboard(tryResponse)}
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                    {#if tryResponseError}
                      <p class="px-3 py-2 text-xs text-red-700 dark:text-red-300">{tryResponseError}</p>
                    {/if}
                    <pre class="max-h-80 overflow-auto p-3 text-xs text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-900">{tryResponse}</pre>
                  </div>
                {/if}
              </div>
            </div>

            <!-- Code examples -->
            <div class="mt-5">
              <div class="mb-2 border-b border-gray-200 dark:border-gray-700">
                <div class="flex">
                  <button
                    class="py-1 px-3 mr-2 text-sm {activeLanguage === 'curl' ? 'border-b-2 border-teal-500 text-teal-600 dark:text-teal-400 font-medium' : 'text-gray-500 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400'}"
                    on:click={() => (activeLanguage = 'curl')}
                  >
                    cURL
                  </button>
                  <button
                    class="py-1 px-3 text-sm {activeLanguage === 'node' ? 'border-b-2 border-teal-500 text-teal-600 dark:text-teal-400 font-medium' : 'text-gray-500 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400'}"
                    on:click={() => (activeLanguage = 'node')}
                  >
                    Node.js
                  </button>
                  <button
                    class="ml-auto py-1 px-3 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    on:click={() => copyToClipboard(activeLanguage === 'curl' ? curlExample : nodeExample)}
                  >
                    Copy
                  </button>
                </div>
              </div>

              {#if activeLanguage === 'curl'}
                <pre class="overflow-x-auto rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3 text-xs text-gray-800 dark:text-gray-200">{curlExample}</pre>
              {:else}
                <pre class="overflow-x-auto rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3 text-xs text-gray-800 dark:text-gray-200">{nodeExample}</pre>
              {/if}
            </div>
          </section>
        {/if}

      </div>
    </main>
  </div>
</div>
